import BookingForm from "@/components/BookingForm";
import { Link } from "react-router-dom";
import { Heart, Users, Building2, Cake } from "lucide-react";
import Seo from "@/components/Seo";

const eventTypes = [
  {
    icon: <Heart className="text-[#b8966a]" size={28} />,
    title: "Weddings",
    desc: "From intimate ceremonies to grand celebrations, we design florals that tell your love story.",
    image: "https://images.unsplash.com/photo-1724847764267-12775ec49657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzc2MjYyMDM5fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: <Building2 className="text-[#b8966a]" size={28} />,
    title: "Corporate Events",
    desc: "Elevate your brand presence with sophisticated floral installations and arrangements.",
    image: "https://images.unsplash.com/photo-1647644520784-7b6ad81c9efd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjBmbG9yYWwlMjBhcnJhbmdlbWVudHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: <Users className="text-[#b8966a]" size={28} />,
    title: "Private Gatherings",
    desc: "Create unforgettable atmospheres for dinner parties, anniversaries, and special occasions.",
    image: "https://images.unsplash.com/photo-1760977606412-e357b1e21c60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbWluaW1hbGlzdCUyMGZsb3dlcnxlbnwwfHx8fDE3NzYyNjIwNjd8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: <Cake className="text-[#b8966a]" size={28} />,
    title: "Celebrations",
    desc: "Birthdays, showers, and milestone moments deserve extraordinary floral touches.",
    image: "https://images.unsplash.com/photo-1655788174766-a63b2d4409d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxibGFjayUyMGJhY2tncm91bmQlMjByZWQlMjByb3NlfGVufDB8fHx8MTc3NjI2MjA2N3ww&ixlib=rb-4.1.0&q=85"
  }
];

export default function Events() {
  return (
    <div className="page-transition pt-[72px]" data-testid="events-page">
      <Seo
        title="Wedding, Event & Corporate Florals in Minneapolis | Fleuris Noir"
        description="Luxury wedding, private event and corporate florals from Fleuris Noir. Custom arrangements, gifting and installations for Minneapolis and the Twin Cities."
        path="/events"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Service",
          serviceType: "Wedding, Event & Corporate Floristry",
          provider: { "@type": "Florist", name: "Fleuris Noir", telephone: "+1-612-547-9260", areaServed: "Minneapolis, Minnesota" },
          areaServed: "Twin Cities Metropolitan Area",
        }}
      />
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1724847764267-12775ec49657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzc2MjYyMDM5fDA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 text-center px-6">
          <p className="overline mb-4">Transformative Design</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#f8f5f0]">
            Event Floristry
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom max-w-3xl text-center">
          <p className="font-body text-lg text-[#a19d98] leading-relaxed">
            We transform spaces into immersive botanical experiences. Whether it's a wedding, 
            corporate gathering, or private celebration, our design team creates floral installations 
            that captivate guests and elevate every moment.
          </p>
        </div>
      </section>

      {/* Event Types */}
      <section className="section bg-[#161616]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="overline mb-4">Our Services</p>
            <h2 className="font-heading text-3xl text-[#f8f5f0]">
              Events We Design
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {eventTypes.map((event, i) => (
              <div 
                key={i} 
                className="group relative h-[350px] overflow-hidden border border-[#b8966a]/10"
                data-testid={`event-type-${i}`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url('${event.image}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="mb-4">{event.icon}</div>
                  <h3 className="font-heading text-2xl text-[#f8f5f0] mb-2">
                    {event.title}
                  </h3>
                  <p className="font-body text-[#a19d98] text-sm">
                    {event.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages CTA */}
      <section className="section bg-[#0c0c0c] border-y border-[#b8966a]/10">
        <div className="container-custom text-center">
          <p className="overline mb-4">Starting From</p>
          <h2 className="font-display text-4xl text-[#f8f5f0] mb-4">$1,000</h2>
          <p className="font-body text-[#a19d98] mb-2 max-w-xl mx-auto">
            Corporate & Private Events from $1,000 | Weddings from $1,500
          </p>
          <p className="font-body text-sm text-[#a19d98] mb-8 max-w-xl mx-auto">
            All packages are fully customized. Inquire for a personalized quote.
          </p>
          <Link to="/collection?category=event" className="btn-secondary" data-testid="view-packages">
            View Event Packages
          </Link>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section bg-[#161616]">
        <div className="container-custom max-w-4xl">
          <BookingForm 
            bookingType="event" 
            title="Inquire About Your Event" 
          />
        </div>
      </section>
    </div>
  );
}
